/**
 * Čisté helpery pro výběr maturitní četby (stránka „Všechny knihy").
 * Parametry jsou strukturální (jen potřebná pole), ne vygenerované API typy,
 * aby šly helpery testovat bez codegenu.
 */

export const GRADUATION_PERIOD_ORDER = ["18STOL", "19STOL", "SVET2021", "CESKA2021"];
export const GRADUATION_TYPE_ORDER = ["PROZA", "POEZIE", "DRAMA"];

/** Celkový počet děl, který má student vybrat — Cermat pravidlo, backend ho neposílá. */
export const TOTAL_REQUIRED_BOOKS = 20;

interface IPeriodLike {
  internalCode?: string | null;
}

interface IBookLike {
  id: string;
  graduationReadingPeriodId?: string | null;
  graduationReadingTypeId?: string | null;
  hasDetail?: boolean | null;
}

interface ICategoryLike {
  id: string;
  minBooks?: number | null;
}

export interface ISelectionStatus {
  /** Sedí celkový počet i všechna minima — teprve pak je seznam „v pořádku". */
  isComplete: boolean;
  /** Kolik knih chybí do `TOTAL_REQUIRED_BOOKS`. */
  missingTotal: number;
  /** Kolik období je pod svým minimem. */
  incompletePeriods: number;
  /** Kolik druhů (próza / poezie / drama) je pod svým minimem. */
  incompleteTypes: number;
}

/**
 * Stabilní řazení číselníku podle pole internalCode; položky s neznámým
 * nebo chybějícím kódem jdou nakonec v původním pořadí.
 */
export const sortByInternalCode = <T extends IPeriodLike>(items: T[], order: string[]): T[] => {
  const orderIndex = (item: IPeriodLike) => {
    const index = order.indexOf(item.internalCode ?? "");
    return index === -1 ? order.length : index;
  };

  return [...items].sort((a, b) => orderIndex(a) - orderIndex(b));
};

/** Řazení období podle GRADUATION_PERIOD_ORDER. */
export const sortGraduationPeriods = <T extends IPeriodLike>(periods: T[]): T[] =>
  sortByInternalCode(periods, GRADUATION_PERIOD_ORDER);

/**
 * Seskupí knihy podle graduationReadingPeriodId. Knihy bez období nebo
 * s obdobím mimo číselník zahodí (CMS drafty se nezobrazují).
 */
export const groupBooksByPeriod = <T extends IBookLike>(books: T[], periodIds: string[]): Record<string, T[]> => {
  const grouped: Record<string, T[]> = {};
  for (const periodId of periodIds) {
    grouped[periodId] = [];
  }

  for (const book of books) {
    const periodId = book.graduationReadingPeriodId;
    if (typeof periodId === "string" && periodId in grouped) {
      grouped[periodId].push(book);
    }
  }

  return grouped;
};

/** Rozdělí knihy na zpracované a nezpracované („nemáme ho zpracované" = hasDetail !== true). */
export const splitBooksByDetail = <T extends IBookLike>(books: T[]): { processed: T[]; unprocessed: T[] } => {
  const processed: T[] = [];
  const unprocessed: T[] = [];

  for (const book of books) {
    (book.hasDetail === true ? processed : unprocessed).push(book);
  }

  return { processed, unprocessed };
};

/** X do čipu „vybráno X · min. Y" — kolik knih daného období je ve výběru. */
export const countSelectedBooks = (periodBooks: IBookLike[], selectedBookIds: ReadonlySet<string>): number =>
  periodBooks.filter(book => selectedBookIds.has(book.id)).length;

/**
 * Je výběr hotový, a když ne, tak v čem.
 *
 * AIDEV-NOTE: Tři podmínky, ne jedna. Součet minim období (16) i minim druhů je NIŽŠÍ než
 * `TOTAL_REQUIRED_BOOKS` (20), takže se kritéria rozcházejí na obě strany: student může mít
 * 20 knih s nezaplněným obdobím, nebo všechna období splněná a do dvacítky mu chybět kniha.
 * Obojí se v aplikaci projevilo tak, že si alert odporoval s barvou nadpisů. Zadání
 * zadavatele 2026-08-18: „minimální počet knih + jednotlivá období + žánry/druhy". [TD-318]
 */
export const getSelectionStatus = (
  books: IBookLike[],
  selectedBookIds: ReadonlySet<string>,
  periods: ICategoryLike[],
  readingTypes: ICategoryLike[],
): ISelectionStatus => {
  const selectedCount = countSelectedBooks(books, selectedBookIds);

  const podMinimem = (kategorie: ICategoryLike[], pole: keyof IBookLike) =>
    kategorie.filter(k => {
      if (typeof k.minBooks !== "number") return false;

      const knihyKategorie = books.filter(book => book[pole] === k.id);

      return !isPeriodComplete(countSelectedBooks(knihyKategorie, selectedBookIds), k.minBooks);
    }).length;

  const incompletePeriods = podMinimem(periods, "graduationReadingPeriodId");
  const incompleteTypes = podMinimem(readingTypes, "graduationReadingTypeId");
  const missingTotal = Math.max(0, TOTAL_REQUIRED_BOOKS - selectedCount);

  return {
    isComplete: missingTotal === 0 && incompletePeriods === 0 && incompleteTypes === 0,
    missingTotal,
    incompletePeriods,
    incompleteTypes,
  };
};

/** Zelený ✓ stav čipu; období bez minima (minBooks null) nikdy „splněno" nehlásí. */
export const isPeriodComplete = (selectedCount: number, minBooks: number | null | undefined): boolean =>
  typeof minBooks === "number" && selectedCount >= minBooks;

/** Nový kompletní výběr po kliknutí na hvězdičku (add/remove), deduplikovaný, bez mutace vstupu. */
export const computeToggledSelection = (currentIds: string[], bookId: string): string[] => {
  const next = new Set(currentIds);
  if (next.has(bookId)) {
    next.delete(bookId);
  } else {
    next.add(bookId);
  }
  return [...next];
};

/** Nejvýš tolik děl od jednoho autora smí student vybrat — Cermat pravidlo, backend ho nehlídá. [TD-417] */
export const MAX_BOOKS_PER_AUTHOR = 2;

// Díla bez konkrétního autora — kdyby se počítala jako jeden autor, šla by vybrat jen dvě.
const AUTHORLESS = new Set(["anonym", "neznámý autor", "lidová slovesnost"]);

interface IAuthoredBookLike {
  id: string;
  authorFullName?: string | null;
}

/**
 * AIDEV-NOTE: Autor nemá v datech id, jen text `author_full_name` z CMS — spoluautoři jsou
 * v něm oddělení „;" (viz `toNaturalName` v todas-cms/scripts/import-books.mjs). Porovnává
 * se proto normalizované jméno a dílo ve dvou se počítá každému autorovi zvlášť. [TD-417]
 */
const authorKeys = (authorFullName: string | null | undefined): string[] =>
  (authorFullName ?? "")
    .split(";")
    .map(name => name.normalize("NFC").replace(/\s+/g, " ").trim().toLocaleLowerCase("cs"))
    .filter(name => name !== "" && !AUTHORLESS.has(name));

/**
 * Přidáním `bookId` by některý z jeho autorů měl ve výběru víc než MAX_BOOKS_PER_AUTHOR děl?
 * Odebírání limit nespouští — student s výběrem uloženým před zavedením pravidla se musí
 * mít jak dostat zpět pod limit. Kniha, kterou `books` nezná, projde.
 */
export const exceedsAuthorLimit = (bookId: string, selectedIds: string[], books: IAuthoredBookLike[]): boolean => {
  if (selectedIds.includes(bookId)) return false;

  const byId = new Map(books.map(book => [book.id, book]));
  const keys = authorKeys(byId.get(bookId)?.authorFullName);
  if (keys.length === 0) return false;

  const counts = new Map<string, number>();
  for (const id of selectedIds) {
    for (const key of authorKeys(byId.get(id)?.authorFullName)) counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return keys.some(key => (counts.get(key) ?? 0) >= MAX_BOOKS_PER_AUTHOR);
};

/**
 * Liší se rozeditovaný výběr od toho, co potvrdil server? Porovnává množiny —
 * server vrací jiné pořadí, než v jakém student klikal, a duplikáty deduplikuje.
 */
export const isSelectionDirty = (saved: string[], draft: string[]): boolean => {
  const savedSet = new Set(saved);
  const draftSet = new Set(draft);

  if (savedSet.size !== draftSet.size) return true;

  return [...draftSet].some(bookId => !savedSet.has(bookId));
};

/** Název druhu (Drama/Próza/Poezie) z číselníku graduationReadingTypes. */
export const getReadingTypeName = (
  typeId: string | null | undefined,
  types: Array<{ id: string; name?: string | null }>,
): string | undefined => types.find(type => type.id === typeId)?.name ?? undefined;

export const LITERARY_KIND_CODES = ["PROZA", "POEZIE", "DRAMA"] as const;
export type TLiteraryKindCode = (typeof LITERARY_KIND_CODES)[number];

/**
 * Řádek „Druh" na detailu knihy (epika / lyrika / drama). CMS pro něj pole nemá,
 * odvozuje se z maturitní klasifikace. Vrací jen známý kód, aby k němu vždy
 * existoval překlad — u neznámého se řádek nevykreslí.
 */
export const getLiteraryKindCode = (
  typeId: string | null | undefined,
  types: Array<{ id: string; internalCode?: string | null }>,
): TLiteraryKindCode | undefined => {
  const code = types.find(type => type.id === typeId)?.internalCode;
  return LITERARY_KIND_CODES.find(known => known === code);
};
